import type { Joint, Site } from "../../types/scene";
import { getAssetEntries } from "../assetStore";
import * as RuntimeRegistry from "../../runtime/objectRegistry";
import { sanitizeMjcfName } from "../../utils/mjcfNames";
import { normalizeQuat } from "./xmlGenerationHelpers";
import type { AssetEntryForExport, ExportBodyNode } from "./xmlGenerationTypes";

export const createFindSiteByIdentifier = (
  siteMap: Record<string, Site> | undefined
): ((identifier: string | undefined | null) => Site | null) => {
  return (identifier) => {
    if (!identifier) return null;
    if (siteMap && siteMap[identifier as import("../../types/scene").SiteUUID]) {
      return siteMap[identifier as import("../../types/scene").SiteUUID];
    }
    const normalized = sanitizeMjcfName(identifier);
    const match = Object.values(siteMap || {}).find(
      (candidate) => candidate?.name && sanitizeMjcfName(candidate.name) === normalized
    );
    return match ?? null;
  };
};

export const createLiveFreeJointBodyPoseResolver = ({
  jointMap,
  seedScalarInitQposFromCurrent,
}: {
  jointMap: Record<string, Joint>;
  seedScalarInitQposFromCurrent: boolean;
}): ((body: ExportBodyNode) => { position: number[]; orientation: number[] } | null) => {
  const resolveBodyJointRef = (entry: unknown): Joint | null => {
    if (!entry) return null;
    if (typeof entry === "string") {
      return (jointMap[entry as import("../../types/scene").JointUUID] as Joint) || null;
    }
    if (typeof entry !== "object") return null;
    const jointRef = entry as Partial<Joint>;
    const baseIdentifier = jointRef.uuid ?? jointRef.name;
    if (!baseIdentifier) return jointRef as Joint;
    const byUuid = jointMap[baseIdentifier as import("../../types/scene").JointUUID];
    if (byUuid) return { ...jointRef, ...byUuid } as Joint;
    const normalized = sanitizeMjcfName(String(baseIdentifier));
    const found = Object.values(jointMap).find(
      (j) =>
        j &&
        (j.uuid === baseIdentifier ||
          j.name === baseIdentifier ||
          sanitizeMjcfName(j.name) === normalized)
    );
    return found ? ({ ...jointRef, ...found } as Joint) : (jointRef as Joint);
  };

  const bodyHasFreeJoint = (body: ExportBodyNode): boolean =>
    (Array.isArray(body?.joints) ? body.joints : [])
      .map((ref) => resolveBodyJointRef(ref))
      .some((joint) => joint?.kind === "free");

  return (body) => {
    if (!seedScalarInitQposFromCurrent || !body?.uuid || !bodyHasFreeJoint(body)) {
      return null;
    }
    const registry = RuntimeRegistry.getAll() || {};
    const object =
      registry[String(body.uuid)] || registry[sanitizeMjcfName(String(body.uuid))] || null;
    const pos = object?.position;
    const quat = object?.quaternion;
    if (!pos || !quat) return null;
    const position = [Number(pos.x), Number(pos.y), Number(pos.z)];
    const orientation = normalizeQuat([
      Number(quat.w),
      Number(quat.x),
      Number(quat.y),
      Number(quat.z),
    ]);
    if (position.some((value) => !Number.isFinite(value))) return null;
    return { position, orientation };
  };
};

export const createMeshPathResolver = ({
  fileMap,
  meshDefinitions,
}: {
  fileMap: Record<string, unknown>;
  meshDefinitions: Record<string, { fingerprint?: string; name?: string; url?: string } | null>;
}): ((meshUuid: string) => string | null) => {
  return (meshUuid) => {
    if (!meshUuid) return null;
    const keys = Object.keys(fileMap ?? {});
    const meshMeta = meshDefinitions[meshUuid] || null;
    const assetEntries = getAssetEntries() || {};
    const assetEntriesByFingerprint = assetEntries as unknown as Record<
      string,
      AssetEntryForExport
    >;
    const assetEntry =
      typeof meshMeta?.fingerprint === "string"
        ? assetEntriesByFingerprint[meshMeta.fingerprint]
        : null;
    const assetName =
      typeof assetEntry?.name === "string" && assetEntry.name.trim()
        ? assetEntry.name.trim()
        : null;
    const libraryPrefix =
      typeof assetEntry?.sources?.libraryPathPrefix === "string"
        ? assetEntry.sources.libraryPathPrefix.trim()
        : "";
    const assetLibraryPath =
      assetName && libraryPrefix
        ? `${libraryPrefix.replace(/\/+$/, "")}/${assetName.replace(/^\/+/, "")}`
        : null;

    const candidate = meshMeta?.url;
    if (candidate) {
      const candidateLower = String(candidate).toLowerCase();
      if (!candidateLower.includes("/")) {
        const prefixed = keys.find((key) => key.toLowerCase().endsWith(`/${candidateLower}`));
        if (prefixed) return prefixed;
      }
      if (keys.includes(candidate)) return candidate;
      if (keys.includes(`assets/${candidate}`)) return `assets/${candidate}`;
      if (assetLibraryPath) return assetLibraryPath;
      if (assetName) return assetName;
      return candidate;
    }

    if (assetLibraryPath) return assetLibraryPath;
    if (assetName) {
      if (keys.includes(assetName)) return assetName;
      if (keys.includes(`assets/${assetName}`)) return `assets/${assetName}`;
      return assetName;
    }

    const meshName = meshMeta?.name ? String(meshMeta.name) : "";
    if (meshName) {
      const stem = meshName.toLowerCase();
      const exactMatch = keys.find((key) => {
        const lower = key.toLowerCase();
        return lower.endsWith(`${stem}.obj`) || lower.endsWith(`${stem}.stl`);
      });
      if (exactMatch) return exactMatch;
    }

    return null;
  };
};

export const createHFieldPathResolver = ({
  fileMap,
  hfieldDefinitions,
}: {
  fileMap: Record<string, unknown>;
  hfieldDefinitions: Record<string, { file?: string } | null>;
}): ((name: string) => string | null) => {
  return (name) => {
    const hfield = hfieldDefinitions[name] || null;
    const candidate = hfield?.file;
    if (!candidate) return null;
    const keys = Object.keys(fileMap);
    if (fileMap[candidate]) return candidate;
    if (fileMap[`assets/${candidate}`]) return `assets/${candidate}`;
    const targetBase = String(candidate)
      .replace(/^.*[\\/]/, "")
      .toLowerCase();
    return (
      keys.find(
        (key) =>
          String(key)
            .replace(/^.*[\\/]/, "")
            .toLowerCase() === targetBase
      ) || candidate
    );
  };
};
